import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import http from '../../http-common';
import { getMe } from '../../api/users';

// The access token lives in Redux memory only (not persisted), so a hard
// refresh loses it. On mount, silently try to trade the httpOnly refresh
// cookie for a new access token and restore the session before any route
// renders -- see AuthRoutes, which waits on authStatus.checked before
// deciding whether to redirect, so this never causes a flash to /login.
// Protected routes show a spinner until the check lands, so a hung refresh
// must not leave them spinning forever: give up and treat it as logged out.
export const AUTH_CHECK_TIMEOUT_MS = 10000;

export default function AuthBootstrap({ children }) {
  const dispatch = useDispatch();

  useEffect(() => {
    let cancelled = false;
    // Set once the check is resolved, either way. A refresh that answers after
    // the timeout is ignored rather than logging the user in behind /login.
    let settled = false;

    const markChecked = () => {
      if (cancelled || settled) return;
      settled = true;
      dispatch({ type: 'AUTH_CHECKED' });
    };
    const timer = setTimeout(markChecked, AUTH_CHECK_TIMEOUT_MS);

    const bootstrap = async () => {
      try {
        const { data } = await http.post('/users/refresh');
        // The store has no token yet, so pass the fresh one explicitly.
        const profile = await getMe({ headers: { Authorization: `Bearer ${data.token}` } });
        if (!cancelled && !settled) {
          const { _id, ...rest } = profile;
          dispatch({ type: 'LOGIN', payload: { id: _id, ...rest, token: data.token } });
        }
      } catch {
        // No valid refresh cookie (never logged in, expired, or revoked) -- stay logged out.
      } finally {
        clearTimeout(timer);
        markChecked();
      }
    };

    bootstrap();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [dispatch]);

  return children;
}
